import {
  sanitizeLayoutFieldBinds,
  type LayoutFieldBinds,
} from './layoutFieldBinds';
import type { PostLayoutBlob, PostLayoutConfig, PostLayoutKind } from './types';

export interface PersistedPostLayoutBlob extends PostLayoutBlob {
  field_binds?: LayoutFieldBinds;
}

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

export function buildPersistedPostLayout({
  postLayout,
  fieldBinds,
}: {
  postLayout: PostLayoutConfig;
  fieldBinds: LayoutFieldBinds;
}): PersistedPostLayoutBlob {
  const { kind, ...config } = postLayout;
  const sanitized = sanitizeLayoutFieldBinds(kind, fieldBinds);
  return {
    kind,
    config: config as Record<string, unknown>,
    ...(Object.keys(sanitized).length > 0 ? { field_binds: sanitized } : {}),
  };
}

export function parsePersistedPostLayout(value: unknown): {
  postLayout: PostLayoutConfig;
  fieldBinds: LayoutFieldBinds;
} | null {
  const persisted = asRecord(value);
  if (!persisted || typeof persisted.kind !== 'string') return null;
  const config = asRecord(persisted.config) ?? {};
  const postLayout = { kind: persisted.kind, ...config } as PostLayoutConfig;
  const rawBinds = asRecord(persisted.field_binds) ?? {};
  const fieldBinds = sanitizeLayoutFieldBinds(
    persisted.kind as PostLayoutKind,
    rawBinds as LayoutFieldBinds,
  );
  return { postLayout, fieldBinds };
}
